import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  DataListItem,
  Dialog,
  Input,
  Input2FA,
} from '@node-space/ui-components'
import RequiredAppCallout from 'components/RequiredAppCallout'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { useCreateTransferMutation } from 'hooks/mutations/useCreateTransferMutation'
import { usePayoutMutation } from 'hooks/mutations/usePayoutMutation'
import { usePayoutsMutation } from 'hooks/mutations/usePayoutsMutation'
import { usePayoutsV2PostAuthMutation } from 'hooks/mutations/usePayoutsV2Mutation'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse, BeneficiaryEntityType, BeneficiaryType } from 'types/beneficiaries'
import { PayoutsV2PostAuthRequest } from 'types/payoutsV2'
import { Currencies } from 'types/types'
import {
  PayoutInstructionTypes,
  PayoutRequest,
  TransferRequest,
} from 'types/unifiedPayouts/unifiedPayouts'
import { roundNumberWithCommas } from 'utils/utils'
import { logLegacyFiatPayout } from '../FiatSend/utils'
import {
  selectedBvnkNetworkBeneficiaryAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from './atoms/atoms'
import { PayActionProps, PayForm } from './Pay'
import { getPayV1Request, getRecipientName, getThirdPartyPayoutRequest } from './utils/payFormUtils'

const errorCodes = {
  INSUFFICIENT_FUNDS: 'insufficientFunds',
  INVALID_RECEIVER: 'invalidReceiver',
}

const PayConfirm = ({
  dismissAction,
  setStep,
  form,
  payFees,
  registerFormField,
}: PayActionProps) => {
  const selectedFiatBeneficiary = useAtomValue(selectedFiatBeneficiaryAtom)
  const selectedBvnkNetworkBeneficiary = useAtomValue(selectedBvnkNetworkBeneficiaryAtom)
  const sourceWallet = useAtomValue(selectedWalletAtom)

  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()
  const { mutate: payBeneficiary, isPending: isSubmittingPayRequest } = usePayoutMutation()

  // Fiat payment
  const { mutate: submitPayoutRequest, isPending: isPayoutRequestLoading } = usePayoutsMutation()
  const { mutate: submitPayPostAuthRequest, isPending: isPayPostAuthRequestLoading } =
    usePayoutsV2PostAuthMutation()

  // Bvnk NetworkTransfer
  const { mutate: submitBvnkNetworkTransfer, isPending: isSubmittingBvnkNetworkTransfer } =
    useCreateTransferMutation()

  const queryClient = useQueryClient()
  const formValues = form.watch()
  const [submitBeneficiary, setSubmitBeneficiary] = useState(false)
  const [authRequest, setAuthRequest] = useState<PayoutsV2PostAuthRequest>(null)

  useEffect(() => {
    submitBeneficiary && onSubmitPostRequest()
  }, [submitBeneficiary])

  const onSubmitPostRequest = () => {
    const payoutsPostRequest: PayoutsV2PostAuthRequest = {
      challenge: authRequest?.challenge,
      tokenResponse: authRequest?.tokenResponse,
      mfaCallbackUrl: authRequest.mfaCallbackUrl,
    }

    submitPayPostAuthRequest(payoutsPostRequest, {
      onSuccess: () => {
        setStep('complete')
        queryClient.invalidateQueries({ queryKey: ['walletTransactions'] })
      },
      onError: onErrorHandler,
      onSettled: () => {
        setSubmitBeneficiary(false)
      },
    })
  }

  const isPayoutsV2Enabled = !!sourceWallet?.supportedTransferDestinations?.length

  const isLoading = isPayoutsV2Enabled
    ? isPayoutRequestLoading || isPayPostAuthRequestLoading || isSubmittingBvnkNetworkTransfer
    : isSubmittingPayRequest || isSubmittingBvnkNetworkTransfer

  const isBvnkNetworkPayment = !!selectedBvnkNetworkBeneficiary && !selectedFiatBeneficiary

  /**
   * Pay funds via API
   */

  const onErrorHandler = (error: BaseErrorResponse) => {
    if (error?.status === 403) {
      form.setError('password', {
        message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
      })
    } else if (!!error?.data?.errorList?.length) {
      const isInsufficientFunds = error?.errorList?.find(
        errorParam => errorParam?.code === errorCodes.INSUFFICIENT_FUNDS
      )

      const isInvalidReceiver = error?.errorList?.find(
        errorParam => errorParam?.code === errorCodes.INVALID_RECEIVER
      )

      if (isInsufficientFunds) {
        form.setError('errorMessage', {
          message: t('payInsufficient'),
        })
      } else if (isInvalidReceiver) {
        form.setError('errorMessage', {
          message: t('paymentError'),
        })
      } else {
        error?.data?.errorList?.forEach(err => {
          form.setError(err?.parameter as keyof PayForm, { message: error?.message })
        })
      }
    } else {
      form.setError('errorMessage', {
        message: t('paymentError'),
      })
    }
  }

  const submitNetworkTransfer = () => {
    const networkTransferRequest: TransferRequest = {
      walletId: sourceWallet?.walletId,
      amount: {
        value: formValues?.amount?.toString(),
        currency: sourceWallet?.currency,
      },
      paymentReference: formValues?.reference,
      instruction: {
        type: PayoutInstructionTypes.WALLET,
        beneficiaryAccountReference: selectedBvnkNetworkBeneficiary?.details?.accountReference,
      },
    }

    submitBvnkNetworkTransfer(networkTransferRequest, {
      onSuccess: () => {
        setStep('complete')
        queryClient.invalidateQueries({ queryKey: ['walletTransactions'] })
      },
      onError: (error: BaseErrorResponse) => {
        if (error?.status === 428) {
          form?.setValue('challenge', error?.data?.challenge?.value)
          setSubmitBeneficiary(true)
        } else if (error?.data?.errorList) {
          error.data.errorList.forEach(err => {
            form.setError(err?.parameter as keyof PayForm, { message: error?.message })
          })
        } else {
          form.setError('errorMessage', {
            message: t('paymentError'),
          })
        }
      },
    })
  }

  const submitFiatPayout = () => {
    const payoutsPrerequest: PayoutRequest = getThirdPartyPayoutRequest(formValues, sourceWallet)
    submitPayoutRequest(payoutsPrerequest, {
      onError(error: BaseErrorResponse) {
        if (error?.status === 428) {
          setAuthRequest({
            challenge: error?.data?.challenge?.value,
            tokenResponse: formValues?.password,
            mfaCallbackUrl: error?.data?.callbackUrl,
          })
          form?.setValue('challenge', error?.data?.challenge?.value)
          setSubmitBeneficiary(true)
        } else if (error?.data?.errorList) {
          error.data.errorList.forEach(err => {
            form.setError(err?.parameter as keyof PayForm, { message: error?.message })
          })
        } else if (error?.status === 403) {
          form.setError('password', {
            message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
          })
        } else {
          form.setError('errorMessage', {
            message: t('paymentError'),
          })
        }
      },
    })
  }

  const onSubmitV1: SubmitHandler<typeof formValues> = async () => {
    if (isBvnkNetworkPayment) {
      submitNetworkTransfer()
    } else {
      const payRequest = getPayV1Request(formValues, selectedFiatBeneficiary, twoFactorEnabled)

      payBeneficiary(
        { walletId: sourceWallet?.legacyWalletId?.toString(), payRequest },
        {
          onSuccess: () => {
            setStep('complete')
            queryClient.invalidateQueries({ queryKey: ['walletTransactions'] })
            queryClient.invalidateQueries({ queryKey: [reactQueryKeys.customerWallets] })
            queryClient.invalidateQueries({ queryKey: [reactQueryKeys.customerWallet] })
          },
          onError: onErrorHandler,
        }
      )

      const wallet = {
        name: sourceWallet?.walletName,
        currency: sourceWallet?.currency,
        supportedTransferDestinations: sourceWallet?.supportedTransferDestinations,
        id: sourceWallet?.legacyWalletId,
        lsid: sourceWallet?.walletId,
      }

      const beneficiaryName =
        selectedFiatBeneficiary?.entityType === BeneficiaryEntityType.BUSINESS
          ? selectedFiatBeneficiary?.businessName
          : `${selectedFiatBeneficiary?.firstName} ${selectedFiatBeneficiary?.lastName}`
      const beneficiary = {
        name: beneficiaryName,
        alias: selectedFiatBeneficiary?.alias,
        transferDestination: selectedFiatBeneficiary?.transferDestination,
        currency: selectedFiatBeneficiary?.currency,
        reference: selectedFiatBeneficiary?.reference,
        entityType: selectedFiatBeneficiary?.entityType,
        isThirdParty: selectedFiatBeneficiary?.beneficiaryType === BeneficiaryType.THIRD_PARTY,
      }

      logLegacyFiatPayout(wallet, beneficiary)
    }
  }

  const onSubmitV2: SubmitHandler<typeof formValues> = async () => {
    if (isBvnkNetworkPayment) {
      submitNetworkTransfer()
    } else {
      submitFiatPayout()
    }
  }

  useEffect(() => {
    const password = formValues?.password
    if (password?.length === TWO_FA_REQUIRED_LENGTH && twoFactorEnabled) {
      if (isPayoutsV2Enabled) {
        onSubmitV2(formValues)
      } else {
        onSubmitV1(formValues)
      }
    }
  }, [formValues?.password])

  const recipientName = getRecipientName(selectedFiatBeneficiary)

  const toBeneficiary = isBvnkNetworkPayment
    ? selectedBvnkNetworkBeneficiary?.details?.name
    : `${recipientName} | ${selectedFiatBeneficiary?.fiat?.accountNumber}`

  const isGBPBeneficiary = sourceWallet?.currency === Currencies.GBP

  const fees = useMemo(() => {
    return payFees?.fee?.value ?? 0
  }, [payFees])

  return (
    <Dialog
      open
      title={t('reviewPayment')}
      onClose={dismissAction}
      type="utility"
      ActionButtons={
        (!twoFactorEnabled || isBvnkNetworkPayment) && [
          <Button
            key="confirm"
            onClick={form.handleSubmit(isPayoutsV2Enabled ? onSubmitV2 : onSubmitV1)}
            loading={isLoading}
            disabled={isBvnkNetworkPayment ? false : !formValues?.password}
            testId={t('confirmPayment')}
          >
            {t('confirmPayment')}
          </Button>,
          <Button key="back" variant="secondary" onClick={() => setStep('amount')}>
            {t('back')}
          </Button>,
        ]
      }
    >
      <>
        {/* error */}
        {form?.formState?.errors?.errorMessage && (
          <Box className="m-6 my-5">
            <Callout variant="error">{form.formState.errors.errorMessage.message}</Callout>
          </Box>
        )}

        {/* pay summary */}
        <form>
          <DataListItem
            id="amount-to-pay"
            testId="amount-to-pay"
            title={t('amountToPay')}
            body={`${roundNumberWithCommas(Number(formValues?.amount) + (fees ?? 0))} ${
              sourceWallet?.currency
            }`}
          />
          <DataListItem
            id="transaction-fee"
            testId="transaction-fee"
            title={t('fee')}
            body={`${roundNumberWithCommas(fees ?? 0)} ${sourceWallet?.currency}`}
          />

          <DataListItem
            id="total"
            testId="total"
            title={t('amountToBeReceived')}
            body={`${roundNumberWithCommas(formValues?.amount)} ${sourceWallet?.currency}`}
          />

          <DataListItem
            id="from-account"
            testId="from-account"
            title={t('fromWallet')}
            body={sourceWallet?.walletName}
          />

          <DataListItem
            id="to-recipient"
            testId="to-recipient"
            title={t('toBeneficiary')}
            body={toBeneficiary}
          />

          <DataListItem
            id="reference"
            testId="reference"
            title={t('beneficiaryPaymentReference')}
            body={formValues?.reference}
          />

          {isGBPBeneficiary && (
            <Box paddingB={24}>
              <RequiredAppCallout />
            </Box>
          )}

          {!isBvnkNetworkPayment && (
            <Box flexCol gap={16}>
              <p className="text-body-regular text-foreground-prominent">
                {twoFactorEnabled ? t('authCodeConfirm') : t('authPasswordConfirm')}
              </p>
              {/* security input */}
              {twoFactorEnabled ? (
                <Input2FA
                  {...registerFormField('password')}
                  onChange={value => form.setValue('password', value)}
                  error={!!form.formState?.errors?.password && t('incorrectOtpCode')}
                  loading={isLoading}
                />
              ) : (
                <Input
                  placeholder={t('password')}
                  type="password"
                  testId="password"
                  disabled={isLoading}
                  {...registerFormField('password')}
                  onChange={e => {
                    form.setValue('password', e.target.value)
                    form.clearErrors('errorMessage')
                  }}
                  value={formValues?.password}
                  error={!!form.formState?.errors?.password}
                  errorText={form.formState?.errors?.password?.message}
                />
              )}
            </Box>
          )}
        </form>
      </>
    </Dialog>
  )
}

export default PayConfirm
